import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type {
  SeatingConfiguration,
  SeatingState,
  SeatingTable,
  SeatingTestGuest,
} from '@/lib/types'
import {
  cloneSeatingState,
  normalizeRoomConfig,
  personToCoupleId,
  splitAssignments,
  type CouplePerson,
} from '@/lib/seatingUtils'

export interface UseSeatingResult {
  configurations: SeatingConfiguration[]
  activeConfigurationId: string | null
  activeConfigurationName: string
  savedState: SeatingState | null
  loading: boolean
  saving: boolean
  configBusy: boolean
  promoting: boolean
  error: string | null
  selectConfiguration: (id: string) => Promise<void>
  createConfiguration: (name: string, duplicateFromId?: string) => Promise<boolean>
  updateConfigurationName: (name: string) => Promise<boolean>
  deleteConfiguration: (id: string) => Promise<boolean>
  saveSeating: (state: SeatingState) => Promise<boolean>
  promoteTestGuest: (testGuestId: string, state: SeatingState) => Promise<boolean>
  refetch: () => Promise<void>
}

async function loadConfigurationState(configurationId: string): Promise<SeatingState> {
  const [configResult, tablesResult, guestResult, coupleResult, testGuestsResult] =
    await Promise.all([
      supabase
        .from('seating_configurations')
        .select('room_width, room_height, table_diameter')
        .eq('id', configurationId)
        .single(),
      supabase
        .from('seating_tables')
        .select('*')
        .eq('configuration_id', configurationId)
        .order('sort_order', { ascending: true }),
      supabase
        .from('guest_seating_assignments')
        .select('*')
        .eq('configuration_id', configurationId),
      supabase
        .from('couple_seating_assignments')
        .select('*')
        .eq('configuration_id', configurationId),
      supabase
        .from('seating_test_guests')
        .select('*, seating_test_additional_guests(*)')
        .eq('configuration_id', configurationId)
        .order('created_at', { ascending: true }),
    ])

  if (configResult.error) throw configResult.error
  if (tablesResult.error) throw tablesResult.error
  if (guestResult.error) throw guestResult.error
  if (coupleResult.error) throw coupleResult.error
  if (testGuestsResult.error) throw testGuestsResult.error

  const assignments: Record<string, string | null> = {}
  for (const row of guestResult.data ?? []) {
    assignments[row.guest_id] = row.table_id
  }
  for (const row of coupleResult.data ?? []) {
    assignments[personToCoupleId(row.person as CouplePerson)] = row.table_id
  }

  const testGuests: SeatingTestGuest[] = (testGuestsResult.data ?? []).map((row) => {
    const { seating_test_additional_guests, ...guestRow } = row as SeatingTestGuest & {
      seating_test_additional_guests?: SeatingTestGuest['additional_guests']
    }
    const additionalGuests = (seating_test_additional_guests ?? []).sort(
      (a, b) => a.sort_order - b.sort_order
    )
    assignments[guestRow.id] = guestRow.table_id
    return {
      ...guestRow,
      additional_guests: additionalGuests,
    }
  })

  return {
    configurationId,
    tables: tablesResult.data ?? [],
    assignments,
    testGuests,
    room: normalizeRoomConfig({
      roomWidth: configResult.data?.room_width,
      roomHeight: configResult.data?.room_height,
      tableDiameter: configResult.data?.table_diameter,
    }),
  }
}

async function saveTestGuests(state: SeatingState): Promise<void> {
  const configurationId = state.configurationId
  const testGuestIds = new Set(state.testGuests.map((tg) => tg.id))

  const { data: existingTestGuests, error: fetchError } = await supabase
    .from('seating_test_guests')
    .select('id')
    .eq('configuration_id', configurationId)

  if (fetchError) throw fetchError

  const toDelete = (existingTestGuests ?? [])
    .map((row) => row.id)
    .filter((id) => !testGuestIds.has(id))

  if (toDelete.length > 0) {
    const { error: deleteError } = await supabase
      .from('seating_test_guests')
      .delete()
      .in('id', toDelete)

    if (deleteError) throw deleteError
  }

  for (const testGuest of state.testGuests) {
    const tableId = state.assignments[testGuest.id] ?? null

    const { error: upsertError } = await supabase.from('seating_test_guests').upsert({
      id: testGuest.id,
      configuration_id: configurationId,
      primary_name: testGuest.primary_name,
      table_id: tableId,
    })

    if (upsertError) throw upsertError

    const { error: clearAdditionalError } = await supabase
      .from('seating_test_additional_guests')
      .delete()
      .eq('test_guest_id', testGuest.id)

    if (clearAdditionalError) throw clearAdditionalError

    const additionalRows = (testGuest.additional_guests ?? []).map((ag, index) => ({
      id: ag.id,
      test_guest_id: testGuest.id,
      name: ag.name,
      type: ag.type,
      age: ag.age,
      sort_order: index,
    }))

    if (additionalRows.length > 0) {
      const { error: insertAdditionalError } = await supabase
        .from('seating_test_additional_guests')
        .insert(additionalRows)

      if (insertAdditionalError) throw insertAdditionalError
    }
  }
}

async function saveConfigurationState(state: SeatingState): Promise<void> {
  const configurationId = state.configurationId
  const testGuestIds = new Set(state.testGuests.map((tg) => tg.id))

  const { data: existingTables, error: fetchError } = await supabase
    .from('seating_tables')
    .select('id')
    .eq('configuration_id', configurationId)

  if (fetchError) throw fetchError

  const existingIds = new Set((existingTables ?? []).map((t) => t.id))
  const newIds = new Set(state.tables.map((t) => t.id))
  const toDelete = [...existingIds].filter((id) => !newIds.has(id))

  if (toDelete.length > 0) {
    const { error: deleteError } = await supabase.from('seating_tables').delete().in('id', toDelete)
    if (deleteError) throw deleteError
  }

  if (state.tables.length > 0) {
    const tableRows = state.tables.map((t: SeatingTable) => ({
      id: t.id,
      configuration_id: configurationId,
      name: t.name,
      sort_order: t.sort_order,
      position_x: t.position_x ?? null,
      position_y: t.position_y ?? null,
    }))

    const { error: upsertError } = await supabase.from('seating_tables').upsert(tableRows)
    if (upsertError) throw upsertError
  }

  const room = normalizeRoomConfig(state.room)
  const { error: roomError } = await supabase
    .from('seating_configurations')
    .update({
      room_width: room.roomWidth,
      room_height: room.roomHeight,
      table_diameter: room.tableDiameter,
    })
    .eq('id', configurationId)

  if (roomError) throw roomError

  const { guestAssignments, coupleAssignments } = splitAssignments(state.assignments, testGuestIds)

  const { error: clearGuestError } = await supabase
    .from('guest_seating_assignments')
    .delete()
    .eq('configuration_id', configurationId)

  if (clearGuestError) throw clearGuestError

  const guestRows = Object.entries(guestAssignments)
    .filter(([, tableId]) => tableId !== null)
    .map(([guestId, tableId]) => ({
      configuration_id: configurationId,
      guest_id: guestId,
      table_id: tableId,
    }))

  if (guestRows.length > 0) {
    const { error: insertGuestError } = await supabase
      .from('guest_seating_assignments')
      .insert(guestRows)

    if (insertGuestError) throw insertGuestError
  }

  const { error: clearCoupleError } = await supabase
    .from('couple_seating_assignments')
    .delete()
    .eq('configuration_id', configurationId)

  if (clearCoupleError) throw clearCoupleError

  const coupleRows = (['jan', 'michael'] as CouplePerson[])
    .filter((person) => coupleAssignments[person])
    .map((person) => ({
      configuration_id: configurationId,
      person,
      table_id: coupleAssignments[person]!,
    }))

  if (coupleRows.length > 0) {
    const { error: insertCoupleError } = await supabase
      .from('couple_seating_assignments')
      .insert(coupleRows)

    if (insertCoupleError) throw insertCoupleError
  }

  await saveTestGuests(state)
}

async function duplicateTestGuests(
  sourceId: string,
  targetId: string,
  tableIdMap: Map<string, string>
): Promise<void> {
  const { data: sourceTestGuests, error } = await supabase
    .from('seating_test_guests')
    .select('*, seating_test_additional_guests(*)')
    .eq('configuration_id', sourceId)

  if (error) throw error

  for (const row of sourceTestGuests ?? []) {
    const { seating_test_additional_guests, ...guestRow } = row as SeatingTestGuest & {
      seating_test_additional_guests?: SeatingTestGuest['additional_guests']
    }
    const newGuestId = crypto.randomUUID()
    const mappedTableId =
      guestRow.table_id && tableIdMap.has(guestRow.table_id)
        ? tableIdMap.get(guestRow.table_id)!
        : null

    const { error: insertGuestError } = await supabase.from('seating_test_guests').insert({
      id: newGuestId,
      configuration_id: targetId,
      primary_name: guestRow.primary_name,
      table_id: mappedTableId,
    })

    if (insertGuestError) throw insertGuestError

    const additionalRows = (seating_test_additional_guests ?? []).map((ag, index) => ({
      test_guest_id: newGuestId,
      name: ag.name,
      type: ag.type,
      age: ag.age,
      sort_order: index,
    }))

    if (additionalRows.length > 0) {
      const { error: insertAdditionalError } = await supabase
        .from('seating_test_additional_guests')
        .insert(additionalRows)

      if (insertAdditionalError) throw insertAdditionalError
    }
  }
}

async function duplicateConfigurationData(sourceId: string, targetId: string): Promise<void> {
  const sourceState = await loadConfigurationState(sourceId)
  const tableIdMap = new Map<string, string>()

  const newTables = sourceState.tables.map((table) => {
    const newId = crypto.randomUUID()
    tableIdMap.set(table.id, newId)
    return {
      id: newId,
      configuration_id: targetId,
      name: table.name,
      sort_order: table.sort_order,
      position_x: table.position_x ?? null,
      position_y: table.position_y ?? null,
    }
  })

  if (newTables.length > 0) {
    const { error } = await supabase.from('seating_tables').insert(newTables)
    if (error) throw error
  }

  const { error: roomError } = await supabase
    .from('seating_configurations')
    .update({
      room_width: sourceState.room.roomWidth,
      room_height: sourceState.room.roomHeight,
      table_diameter: sourceState.room.tableDiameter,
    })
    .eq('id', targetId)

  if (roomError) throw roomError

  const testGuestIds = new Set(sourceState.testGuests.map((tg) => tg.id))
  const { guestAssignments, coupleAssignments } = splitAssignments(
    sourceState.assignments,
    testGuestIds
  )

  const guestRows = Object.entries(guestAssignments)
    .filter(([, tableId]) => tableId && tableIdMap.has(tableId))
    .map(([guestId, tableId]) => ({
      configuration_id: targetId,
      guest_id: guestId,
      table_id: tableIdMap.get(tableId!)!,
    }))

  if (guestRows.length > 0) {
    const { error } = await supabase.from('guest_seating_assignments').insert(guestRows)
    if (error) throw error
  }

  const coupleRows = (['jan', 'michael'] as CouplePerson[])
    .filter((person) => {
      const tableId = coupleAssignments[person]
      return tableId && tableIdMap.has(tableId)
    })
    .map((person) => ({
      configuration_id: targetId,
      person,
      table_id: tableIdMap.get(coupleAssignments[person]!)!,
    }))

  if (coupleRows.length > 0) {
    const { error } = await supabase.from('couple_seating_assignments').insert(coupleRows)
    if (error) throw error
  }

  await duplicateTestGuests(sourceId, targetId, tableIdMap)
}

async function promoteTestGuestToRealGuest(
  testGuestId: string,
  state: SeatingState
): Promise<void> {
  const testGuest = state.testGuests.find((tg) => tg.id === testGuestId)
  if (!testGuest) throw new Error('Testnutzer nicht gefunden')

  const tableId = state.assignments[testGuestId] ?? null
  const configurationId = state.configurationId

  const { data: pinData, error: pinError } = await supabase.rpc('generate_unique_pin')
  if (pinError || !pinData) throw pinError ?? new Error('PIN konnte nicht generiert werden')

  const { data: newGuest, error: insertGuestError } = await supabase
    .from('guests')
    .insert({
      pin: pinData,
      primary_name: testGuest.primary_name,
      rsvp_status: 'coming',
      invite_sent: false,
      created_by: 'admin',
    })
    .select()
    .single()

  if (insertGuestError) throw insertGuestError

  const additionalRows = (testGuest.additional_guests ?? []).map((ag) => ({
    guest_id: newGuest.id,
    name: ag.name,
    type: ag.type,
    age: ag.age,
    added_by: 'admin',
  }))

  if (additionalRows.length > 0) {
    const { error: insertAdditionalError } = await supabase
      .from('additional_guests')
      .insert(additionalRows)

    if (insertAdditionalError) throw insertAdditionalError
  }

  if (tableId) {
    const { error: assignmentError } = await supabase.from('guest_seating_assignments').insert({
      configuration_id: configurationId,
      guest_id: newGuest.id,
      table_id: tableId,
    })

    if (assignmentError) throw assignmentError
  }

  const { error: deleteTestGuestError } = await supabase
    .from('seating_test_guests')
    .delete()
    .eq('id', testGuestId)

  if (deleteTestGuestError) throw deleteTestGuestError
}

export function useSeating(): UseSeatingResult {
  const [configurations, setConfigurations] = useState<SeatingConfiguration[]>([])
  const [activeConfigurationId, setActiveConfigurationId] = useState<string | null>(null)
  const [savedState, setSavedState] = useState<SeatingState | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [configBusy, setConfigBusy] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activeConfigurationName =
    configurations.find((c) => c.id === activeConfigurationId)?.name ?? ''

  const loadConfiguration = useCallback(async (configurationId: string) => {
    const state = await loadConfigurationState(configurationId)
    setActiveConfigurationId(configurationId)
    setSavedState(state)
  }, [])

  const fetchConfigurations = useCallback(
    async (preferredId?: string | null) => {
      const { data, error: listError } = await supabase
        .from('seating_configurations')
        .select('*')
        .order('created_at', { ascending: true })

      if (listError) throw listError

      const configs = data ?? []
      setConfigurations(configs)

      if (configs.length === 0) return null

      const nextId =
        preferredId && configs.some((c) => c.id === preferredId) ? preferredId : configs[0]!.id

      await loadConfiguration(nextId)
      return nextId
    },
    [loadConfiguration]
  )

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      await fetchConfigurations(activeConfigurationId)
    } catch (err) {
      console.error('Error fetching seating:', err)
      setError(err instanceof Error ? err.message : 'Sitzplan konnte nicht geladen werden')
      setSavedState(null)
    } finally {
      setLoading(false)
    }
  }, [activeConfigurationId, fetchConfigurations])

  useEffect(() => {
    refetch()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const selectConfiguration = useCallback(
    async (id: string) => {
      if (id === activeConfigurationId) return
      try {
        setLoading(true)
        setError(null)
        await loadConfiguration(id)
      } catch (err) {
        console.error('Error loading configuration:', err)
        setError(err instanceof Error ? err.message : 'Konfiguration konnte nicht geladen werden')
      } finally {
        setLoading(false)
      }
    },
    [activeConfigurationId, loadConfiguration]
  )

  const saveSeating = useCallback(async (state: SeatingState): Promise<boolean> => {
    try {
      setSaving(true)
      setError(null)
      await saveConfigurationState(state)
      setSavedState(cloneSeatingState(state))
      return true
    } catch (err) {
      console.error('Error saving seating:', err)
      setError(err instanceof Error ? err.message : 'Sitzplan konnte nicht gespeichert werden')
      return false
    } finally {
      setSaving(false)
    }
  }, [])

  const promoteTestGuest = useCallback(
    async (testGuestId: string, state: SeatingState): Promise<boolean> => {
      try {
        setPromoting(true)
        setError(null)
        await saveConfigurationState(state)
        await promoteTestGuestToRealGuest(testGuestId, state)
        await loadConfiguration(state.configurationId)
        return true
      } catch (err) {
        console.error('Error promoting test guest:', err)
        setError(
          err instanceof Error
            ? err.message
            : 'Testnutzer konnte nicht zu echtem Gast promotet werden'
        )
        return false
      } finally {
        setPromoting(false)
      }
    },
    [loadConfiguration]
  )

  const createConfiguration = useCallback(
    async (name: string, duplicateFromId?: string): Promise<boolean> => {
      const trimmedName = name.trim()
      if (!trimmedName) return false

      try {
        setConfigBusy(true)
        setError(null)

        const { data: newConfig, error: insertError } = await supabase
          .from('seating_configurations')
          .insert({ name: trimmedName })
          .select()
          .single()

        if (insertError) throw insertError

        if (duplicateFromId) {
          await duplicateConfigurationData(duplicateFromId, newConfig.id)
        }

        await fetchConfigurations(newConfig.id)
        return true
      } catch (err) {
        console.error('Error creating configuration:', err)
        setError(err instanceof Error ? err.message : 'Konfiguration konnte nicht erstellt werden')
        return false
      } finally {
        setConfigBusy(false)
      }
    },
    [fetchConfigurations]
  )

  const updateConfigurationName = useCallback(
    async (name: string): Promise<boolean> => {
      if (!activeConfigurationId) return false
      const trimmedName = name.trim()
      if (!trimmedName) return false

      try {
        setConfigBusy(true)
        setError(null)

        const { error: updateError } = await supabase
          .from('seating_configurations')
          .update({ name: trimmedName })
          .eq('id', activeConfigurationId)

        if (updateError) throw updateError

        setConfigurations((prev) =>
          prev.map((c) => (c.id === activeConfigurationId ? { ...c, name: trimmedName } : c))
        )
        return true
      } catch (err) {
        console.error('Error updating configuration name:', err)
        setError(
          err instanceof Error ? err.message : 'Konfigurationsname konnte nicht gespeichert werden'
        )
        return false
      } finally {
        setConfigBusy(false)
      }
    },
    [activeConfigurationId]
  )

  const deleteConfiguration = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        setConfigBusy(true)
        setError(null)

        const { error: deleteError } = await supabase
          .from('seating_configurations')
          .delete()
          .eq('id', id)

        if (deleteError) throw deleteError

        const fallbackId = id === activeConfigurationId ? null : activeConfigurationId
        await fetchConfigurations(fallbackId)
        return true
      } catch (err) {
        console.error('Error deleting configuration:', err)
        setError(err instanceof Error ? err.message : 'Konfiguration konnte nicht gelöscht werden')
        return false
      } finally {
        setConfigBusy(false)
      }
    },
    [activeConfigurationId, fetchConfigurations]
  )

  return {
    configurations,
    activeConfigurationId,
    activeConfigurationName,
    savedState,
    loading,
    saving,
    configBusy,
    promoting,
    error,
    selectConfiguration,
    createConfiguration,
    updateConfigurationName,
    deleteConfiguration,
    saveSeating,
    promoteTestGuest,
    refetch,
  }
}
